import { useEffect, useRef, useState } from "react";
import { Camera, UserRound, X } from "lucide-react";
import { toast } from "sonner";

import { cn, initialsFrom } from "lib/utils";
import { Spinner } from "components/ui/spinner";
import AttachmentService from "services/Attachment";
import Validate from "components/Validate";
import { Button } from "@/components/ui/button";

/**
 * Circular profile-picture avatar with click-to-upload.
 *
 * Uploads the chosen file straight to S3 via AttachmentService and reports the
 * resulting attachment metadata through onChange so the parent form can persist
 * it on save. The local object-URL preview is shown immediately for feedback.
 *
 * Clearing is reported through onClear; the parent decides how to reflect it in
 * `value` and how to persist the removal.
 */
function ProfilePictureUpload({
  value,
  name,
  disabled = false,
  size = 128,
  onChange,
  onClear,
}) {
  const inputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);

  const uploading = progress !== null;
  const imageSrc = preview || value;

  const openPicker = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const clearPicture = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    onClear?.();
  };

  const handleFileChange = async event => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again still fires onChange.
    event.target.value = "";
    if (!file) return;

    if (!Validate.imageFileType(file.type)) {
      toast.error("Invalid image type. Supported types: gif, jpg, png");
      return;
    }

    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const objectUrl = URL.createObjectURL(file);
    previewUrlRef.current = objectUrl;
    setPreview(objectUrl);
    setProgress(0);

    try {
      const attachment = await AttachmentService.uploadFile(
        file,
        ({ loaded, total }) =>
          setProgress(total ? Math.round((loaded / total) * 100) : 0)
      );
      onChange?.({ attachment, previewUrl: objectUrl });
    } catch {
      toast.error("Failed to upload profile picture. Please try again.");
      URL.revokeObjectURL(objectUrl);
      previewUrlRef.current = null;
      setPreview(null);
    } finally {
      setProgress(null);
    }
  };

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  return (
    <div className="tw:flex tw:flex-col tw:justify-center tw:items-center  tw:gap-2">
      <div className="tw:relative" style={{ width: size, height: size }}>
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          aria-label="Change profile picture"
          className={cn(
            "tw:group tw:relative tw:h-full tw:w-full tw:overflow-hidden tw:rounded-full tw:border tw:border-(--color-border)",
            "tw:bg-(--color-surface-raised) tw:outline-none tw:transition",
            "tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50",
            disabled
              ? "tw:cursor-default"
              : "tw:cursor-pointer tw:hover:border-(--color-highlight)"
          )}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={name || "Profile picture"}
              className="tw:h-full tw:w-full tw:object-cover tw:rounded-full"
            />
          ) : (
            <span className="tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center tw:bg-(--color-highlight)/10 tw:rounded-full">
              {name ? (
                <span
                  className="tw:font-medium tw:text-(--color-highlight) tw:[font-family:var(--font-heading)]"
                  style={{ fontSize: size * 0.32 }}
                >
                  {initialsFrom(name)}
                </span>
              ) : (
                <UserRound
                  className="tw:text-(--color-text-muted)"
                  style={{ width: size * 0.4, height: size * 0.4 }}
                />
              )}
            </span>
          )}

          {!disabled && !uploading && (
            <span className="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:bg-black/50 tw:text-white tw:opacity-0 tw:transition tw:group-hover:opacity-100 tw:rounded-full">
              <Camera className="tw:size-5" />
              <span className="tw:text-xs tw:font-medium">Change</span>
            </span>
          )}

          {uploading && (
            <span className="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:bg-black/55 tw:text-white tw:rounded-full">
              <Spinner className="tw:size-5" />
              <span className="tw:text-xs tw:font-medium tw:tabular-nums">
                {progress}%
              </span>
            </span>
          )}
        </button>

        {!disabled && !uploading && imageSrc && (
          <Button
            type="button"
            onClick={clearPicture}
            aria-label="Remove profile picture"
            title="Remove profile picture"
            className="tw:absolute tw:-top-2 tw:-right-1 tw:rounded-full"
            variant="destructive"
            size="icon"
          >
            <X className="tw:size-4" />
          </Button>
        )}
      </div>

      {!disabled && (
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading}
          className="tw:text-sm tw:font-medium tw:text-(--color-highlight) tw:hover:underline tw:disabled:opacity-50"
        >
          {imageSrc ? "Change photo" : "Upload photo"}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />
    </div>
  );
}

export default ProfilePictureUpload;
